import { useState, useMemo, useRef, useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import SearchInput, { GeoNames } from "../../SearchInput";
import {
  BellRingIcon,
  CrossIcon,
  MapPinIcon,
  PlusCircleIcon,
  SpinnerIcon,
} from "../../Icons";
import { Contact, DocumentVault } from "../../../types/TradeFlow";
import { useSendEmailNotifyMutation } from "../../../store/apiSlices/emailManagement";

import { toast } from "react-toastify";
import { useGetTradeFlowByIdQuery } from "../../../store/apiSlices/tradeFlow";
import { ContactInfo } from "./Tasks";

interface Props {
  contacts: Contact[];
  documentVault: DocumentVault;
  row_id: string;
  step_id: string;
  toggleModal: () => void;
}

type NotifyContactsFormType = {
  notification_settings: {
    consignee_company_name: boolean;
    loading_date: string;
    port_of_loading: Omit<GeoNames, "adminCode1" | "geonameId">;
    port_of_destination: Omit<GeoNames, "adminCode1" | "geonameId">;
    order_details: {
      label: string;
      value: string;
    }[];
  };
};

export default function NotifyContactsForm({
  contacts,
  row_id,
  step_id,
  documentVault,
  toggleModal,
}: Props) {
  const [selectedEmails, setSelectedEmails] = useState<
    { email: string; name: string }[]
  >([]);

  const [filteredContacts, setFilteredContacts] = useState(() =>
    contacts.filter((el) => el.name && el.email)
  );

  const { data: allTradeFlowSteps } = useGetTradeFlowByIdQuery(row_id, {
    skip: !row_id,
  });
  const [searchParams] = useSearchParams();
  const companyName = searchParams.get("company_name");

  const [sendEmailNotify, { isLoading }] = useSendEmailNotifyMutation();

  const allStepContacts = allTradeFlowSteps
    ? allTradeFlowSteps.data.trade_flow_json.flatMap((s) => s.Contacts)
    : [];

  const toggleEmailSelection = (contact: { email: string; name: string }) => {
    setSelectedEmails((prev) =>
      prev.some((c) => c.email === contact.email)
        ? prev.filter((c) => c.email !== contact.email)
        : [...prev, contact]
    );
  };

  // RHF FORM SETUP
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<NotifyContactsFormType>({
    defaultValues: {
      notification_settings: {
        consignee_company_name: false,
        order_details: [{ label: "", value: "" }], // at least one row
      },
    },
  });

  // dynamic rows for order details
  const { fields, append, remove } = useFieldArray({
    control,
    name: "notification_settings.order_details",
  });

  const addContact = (contact: Contact) => {
    setFilteredContacts((prev) => {
      const alreadyExists = prev.some(
        (c) => c.contact_id === contact.contact_id
      );
      if (alreadyExists) return prev;
      return [...prev, contact];
    });
  };

  // Submit Handler
  const onSubmit = async (formData: NotifyContactsFormType) => {
    const {
      notification_settings: {
        loading_date,
        port_of_loading,
        port_of_destination,
        order_details,
        consignee_company_name,
      },
    } = formData;

    const payload = {
      row_id,
      step_id,
      document_id: documentVault.document_id,
      name: documentVault.document_name,
      contacts: selectedEmails,
      loading_date,
      port_of_loading: `${port_of_loading.name}, ${port_of_loading.countryCode}`,
      port_of_destination: `${port_of_destination.name}, ${port_of_destination.countryCode}`,
      order_details: order_details.reduce((acc, item) => {
        acc[item.label] = item.value;

        return acc;
      }, {} as Record<string, string>),
      consignee_company_name: consignee_company_name ? 1 : 0,
      company_name: companyName,
    };

    const { data } = await sendEmailNotify(payload);

    if (data?.status === "success") {
      toast.success("Notification mail sent successfully");
      toggleModal();
    } else {
      toast.error("Something went wrong! please try again");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      {/* SECTION 1 —  Notify Contacts   */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-700 inline-flex items-center gap-1">
          <BellRingIcon className="size-5" />
          Notify your contacts
        </h3>

        <ContactSearch contacts={allStepContacts} addContact={addContact} />

        <div className="max-h-36 overflow-y-auto p-3 space-y-3">
          {filteredContacts.length > 0 ? (
            filteredContacts.map(({ email, name, contact_id }) => (
              <label
                key={contact_id}
                className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  onChange={() => toggleEmailSelection({ email, name })}
                />
                {email} — ({name})
              </label>
            ))
          ) : (
            <p className="text-center text-gray-400 text-xs">
              You don't have any existing contacts for this step
            </p>
          )}
        </div>
      </div>

      {/*  Notification Settings   */}
      <div className="space-y-[10px]">
        <h3 className="text-sm font-medium text-gray-700">
          Notification Settings{" "}
          <span className="text-xs text-gray-500 font-normal">
            (Included in message)
          </span>
        </h3>

        {/* Boolean checkbox */}
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            {...register("notification_settings.consignee_company_name")}
            className="rounded border-gray-300"
          />
          Include consignee company name
        </label>

        {/* Order Details (Array) */}
        <div className="space-y-3">
          {fields.map((item, index) => {
            const labelError =
              errors?.notification_settings?.order_details?.[index]?.label
                ?.message;

            const valueError =
              errors?.notification_settings?.order_details?.[index]?.value
                ?.message;

            return (
              <div key={item.id} className="flex flex-col gap-1">
                <div className="flex items-center gap-3">
                  {/* LABEL DROPDOWN */}
                  <div className="w-full">
                    <select
                      className={`input-new w-full ${
                        labelError ? "border-red-500" : ""
                      }`}
                      {...register(
                        `notification_settings.order_details.${index}.label`,
                        { required: "Label is required" }
                      )}
                    >
                      <option value="">Select field</option>
                      <option value="invoice_number">Invoice Number</option>
                      <option value="order_number">Order Number</option>
                      <option value="RFQ_number">RFQ Number</option>
                    </select>
                  </div>

                  {/* VALUE INPUT */}
                  <div className="w-full">
                    <input
                      className={`input-new w-full ${
                        valueError ? "border-red-500" : ""
                      }`}
                      placeholder="Value"
                      {...register(
                        `notification_settings.order_details.${index}.value`,
                        { required: "Value is required" }
                      )}
                    />
                  </div>

                  {/* REMOVE BUTTON */}
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="text-red-500"
                    >
                      <CrossIcon className="size-5" />
                    </button>
                  )}

                  {/* ADD BUTTON (on last row only) */}
                  {index === fields.length - 1 && fields.length < 3 && (
                    <button
                      type="button"
                      onClick={() => append({ label: "", value: "" })}
                      className="text-gray-600"
                    >
                      <PlusCircleIcon className="size-6" />
                    </button>
                  )}
                </div>

                {/* ERROR MESSAGES */}
                {(labelError || valueError) && (
                  <p className="text-red-500 text-xs">
                    {labelError || valueError}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Loading Date */}
        <div className="flex gap-2 items-center">
          <label className="text-sm w-[130px]">Loading Date</label>
          <div className="w-full">
            <input
              type="date"
              {...register("notification_settings.loading_date", {
                required: "Loading date is required",
              })}
              className="input-new cursor-pointer"
              onClick={(e) => e.currentTarget.showPicker()}
            />
            {errors?.notification_settings?.loading_date && (
              <p className="text-red-500 text-xs">
                {errors.notification_settings.loading_date.message}
              </p>
            )}
          </div>
        </div>

        {/* Port of Loading */}
        <div className="flex flex-col gap-1">
          <div className="flex gap-2 items-center">
            <label className="text-sm w-[130px]">Port of Loading</label>

            <Controller
              name="notification_settings.port_of_loading"
              control={control}
              rules={{ required: "Port of loading is required" }}
              render={({ field, fieldState }) => (
                <div className="flex flex-col w-full">
                  <SearchInput
                    name={field.name}
                    placeholder="Select port"
                    initialCity={
                      field.value
                        ? `${field.value?.name}, ${field.value?.countryCode}`
                        : ""
                    }
                    icon={<MapPinIcon className="size-5 text-gray-700" />}
                    className={`input-new !pl-[40px] ${
                      fieldState.error ? "border-red-500" : ""
                    }`}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />

                  {fieldState.error && (
                    <p className="text-red-500 text-xs mt-1">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        </div>

        {/* Port of Destination */}
        <div className="flex gap-2 items-center">
          <label className="text-sm w-[130px]">Port of Destination</label>
          <Controller
            name="notification_settings.port_of_destination"
            rules={{ required: "Port of destination is required" }}
            control={control}
            render={({ field, fieldState }) => (
              <div className="flex flex-col w-full">
                <SearchInput
                  name={field.name}
                  placeholder="Select port"
                  initialCity={
                    field.value
                      ? `${field.value?.name}, ${field.value?.countryCode}`
                      : ""
                  }
                  icon={<MapPinIcon className="size-5 text-gray-700" />}
                  className="input-new !pl-[40px]"
                  onChange={field.onChange}
                />

                {fieldState.error && (
                  <p className="text-red-500 text-xs mt-1">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>
      </div>

      {/* Submit Button  */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={selectedEmails.length === 0 || isLoading}
          className={`px-5 py-2 rounded-lg shadow transition ${
            selectedEmails.length === 0
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-custom-blue-medium text-white hover:bg-custom-blue-medium/70"
          }`}
        >
          {isLoading ? (
            <SpinnerIcon className="size-4 animate-spin" />
          ) : (
            "Submit"
          )}
        </button>
      </div>
    </form>
  );
}

function ContactSearch({
  contacts,
  addContact,
}: {
  contacts: Contact[];
  addContact: (contact: Contact) => void;
}) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Valid contacts
  const validContacts = useMemo(
    () =>
      contacts.filter((c) => c.name?.trim() !== "" && c.email?.trim() !== ""),
    [contacts]
  );

  // Filtered + show all when empty
  const filtered = useMemo(() => {
    if (!query.trim()) return validContacts;
    return validContacts.filter(
      (c) =>
        c.name.toLowerCase().includes(query.toLowerCase()) ||
        c.email.toLowerCase().includes(query.toLowerCase())
    );
  }, [query, validContacts]);

  return (
    <div ref={wrapperRef} className="w-full relative">
      {/* Search Input */}
      <input
        type="search"
        placeholder="Search contacts..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setIsOpen(true)} // open dropdown on click/focus
        className="input-new"
      />

      {/* Contacts List */}
      {isOpen && (
        <ul
          className={`absolute mt-1 w-full bg-white ${
            filtered.length > 0 ? "max-h-[130px]" : ""
          } text-xs shadow-lg rounded-md overflow-y-scroll z-50`}
        >
          {filtered.length > 0 ? (
            filtered.map((c) => (
              <li
                key={c.contact_id}
                className="p-3 flex items-center gap-1 bg-white border-b hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  addContact(c);
                  setQuery("");
                  setIsOpen(false); // close after selecting
                }}
              >
                {/* <p className="font-medium text-gray-800">{c.name}</p>
                <p className="text-gray-500">{c.email}</p> */}
                <ContactInfo contact={{ name: c.name, email: c.email }} />
              </li>
            ))
          ) : (
            <p className="text-gray-500 text-center py-1">No contacts found.</p>
          )}
        </ul>
      )}
    </div>
  );
}
